/**
 * useEntityView Hook
 *
 * Builds the friendly entity view model: loads the SHACL view shapes,
 * picks the node shapes that match the entity's rdf:type values and
 * resolves the values of their property shapes. Properties shown with
 * dash:DetailsViewer also get the values of their nested node shape.
 * Hidden rows are read too: they are data for the widgets of other rows,
 * which find them by dview:key. A keyed row with an sh:node is a table:
 * its linked resources with the columns of that node shape.
 */

import { useEffect, useState } from 'react';
import {
  loadViewShapes,
  resolvePropertyValues,
  resolveTable,
  VALUE_LIMIT,
  type KmWindow,
  type SubjectValues,
  type TableValues,
} from '@services/entityView.service';
import {
  DASH,
  DVIEW,
  HERO_VIEWERS,
  heroKeys,
  leadingPredicate,
  mergeProperties,
  nestedNodeShape,
  selectNodeShapes,
  setting,
  settingList,
  type ViewNodeShape,
  type ViewPropertyShape,
  type ViewShapes,
} from '@utils/viewShapes.utils';
import type { EntityProperty } from '@/types';

const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';

export type EntityViewStatus = 'idle' | 'loading' | 'ready' | 'noShape' | 'error';

export interface EntityViewModel {
  status: EntityViewStatus;
  /** Entity the model was built for; differs from the page URI right after navigating. */
  uri: string;
  shapes: ViewShapes | null;
  nodeShapes: ViewNodeShape[];
  /** Visible property shapes (dash:hidden removed), in display order. */
  properties: ViewPropertyShape[];
  /** Every property shape, hidden ones included. */
  allProperties: ViewPropertyShape[];
  /** Property shapes by dview:key. */
  rows: Record<string, ViewPropertyShape>;
  /** Linked resources with their columns, per table row id. */
  tables: Record<string, TableValues>;
  /** Values of the entity per property shape id. */
  values: SubjectValues;
  /** Nested node shape per property shape id (dash:DetailsViewer only). */
  nestedShapes: Record<string, ViewNodeShape>;
  /** Values of nested nodes: node IRI → property shape id → values. */
  nestedValues: Record<string, SubjectValues>;
  /** Property shapes that could not be read without signing in. */
  unavailable: Set<string>;
  /** Predicates of the entity covered by a shape, left out of "other properties". */
  coveredPredicates: Set<string>;
}

const EMPTY: EntityViewModel = {
  status: 'idle',
  uri: '',
  shapes: null,
  nodeShapes: [],
  properties: [],
  allProperties: [],
  rows: {},
  tables: {},
  values: {},
  nestedShapes: {},
  nestedValues: {},
  unavailable: new Set(),
  coveredPredicates: new Set(),
};

export function isTableRow(property: ViewPropertyShape): boolean {
  return Boolean(property.key && property.nodeShapeId && property.viewer !== DASH.DetailsViewer);
}

/** Table rows the page draws: the class's dview:hero rows, the tables they read by key, and every table that is not a hero. */
function tablesToRead(properties: ViewPropertyShape[], hero: string[] | null): ViewPropertyShape[] {
  const tables = properties.filter(isTableRow);
  if (!hero) return tables;
  const chosen = properties.filter((property) => property.key && hero.includes(property.key));
  const read = new Set([...hero, ...chosen.flatMap((property) => [...settingList(property, 'objects'), ...settingList(property, 'lanes')])]);
  return tables.filter((property) => !HERO_VIEWERS.has(property.viewer ?? '') || read.has(property.key ?? ''));
}

/** Metres a structure's km is widened by when its objects are read: its own km and its tracks' km differ a little. */
const WINDOW_PAD = 200;

/**
 * The objects tables a structure drawing reads (dview:objects of a dview:AreaLocationViewer row) only
 * need the objects within the structure's km (its dview:kmFrom and dview:kmTo rows), matched on
 * the objects' km columns (at, track-at).
 */
function kmWindows(shapes: ViewShapes, properties: ViewPropertyShape[]): Map<string, KmWindow> {
  const windows = new Map<string, KmWindow>();
  const byKey = (key: string) => properties.find((property) => property.key === key);
  for (const area of properties.filter((property) => property.viewer === DVIEW.AreaLocationViewer)) {
    const subject = [...settingList(area, 'kmFrom'), ...settingList(area, 'kmTo')]
      .map(byKey)
      .filter((row): row is ViewPropertyShape => Boolean(row))
      .map((row) => row.path);
    for (const key of settingList(area, 'objects')) {
      const table = byKey(key);
      const columns = table ? (shapes.nodeShapesById.get(table.nodeShapeId!)?.properties ?? []) : [];
      const item = columns.filter((column) => column.key === 'at' || column.key === 'track-at').map((column) => column.path);
      if (table) windows.set(table.id, { subject, item, pad: WINDOW_PAD });
    }
  }
  return windows;
}

async function resolveTables(
  uri: string,
  shapes: ViewShapes,
  properties: ViewPropertyShape[],
  hero: string[] | null
): Promise<Record<string, TableValues>> {
  const tables: Record<string, TableValues> = {};
  const windows = kmWindows(shapes, properties);
  await Promise.all(
    tablesToRead(properties, hero).map(async (property) => {
      const columns = shapes.nodeShapesById.get(property.nodeShapeId!)?.properties ?? [];
      const limit = Number(setting(property, 'limit')) || VALUE_LIMIT;
      try {
        tables[property.id] = await resolveTable(uri, property, columns, limit, windows.get(property.id));
      } catch (error) {
        console.warn('[useEntityView] Table row not available:', property.id, error);
      }
    })
  );
  return tables;
}

async function buildModel(
  uri: string,
  entityProperties: EntityProperty[]
): Promise<EntityViewModel> {
  const shapes = await loadViewShapes();
  const types =
    entityProperties
      .find((property) => property.predicate === RDF_TYPE)
      ?.objects.map((o) => o.value) ?? [];
  // The class's own node shape lists its superclasses' properties too (build.py), so the one
  // with the most properties is the most specific: it goes first, for merging and the class name.
  // A class's own view shape names its hero (dview:hero); shared superclass shapes do not
  const nodeShapes = selectNodeShapes(shapes, types).sort(
    (a, b) => Number(Boolean(b.settings?.hero)) - Number(Boolean(a.settings?.hero)) || b.properties.length - a.properties.length
  );
  if (nodeShapes.length === 0) return { ...EMPTY, status: 'noShape', uri, shapes };

  const allProperties = mergeProperties(nodeShapes);
  const properties = allProperties.filter((property) => !property.hidden);
  const coveredPredicates = new Set(
    allProperties.map((property) => leadingPredicate(property.path)).filter(Boolean) as string[]
  );
  coveredPredicates.add(RDF_TYPE);

  const rows: Record<string, ViewPropertyShape> = {};
  for (const property of allProperties) {
    if (!property.key) continue;
    if (rows[property.key] && rows[property.key].id !== property.id) {
      console.warn(`dview:key "${property.key}" is on both ${rows[property.key].id} and ${property.id}; the first is used`);
    }
    rows[property.key] ??= property;
  }

  const [resolved, tables] = await Promise.all([
    resolvePropertyValues(
      [uri],
      allProperties.filter((property) => !isTableRow(property)),
      { [uri]: entityProperties }
    ),
    resolveTables(uri, shapes, allProperties, heroKeys(nodeShapes)),
  ]);
  const values = resolved.values[uri] ?? {};
  // A forward predicate the dim dataset lacks can still come from the federated DESCRIBE
  // (RINF's era: values on the same IRI).
  for (const property of allProperties) {
    if ((values[property.id]?.length ?? 0) > 0 || property.path.kind !== 'predicate') continue;
    const predicate = property.path.iri;
    const described = entityProperties.find((entry) => entry.predicate === predicate)?.objects;
    if (described && described.length > 0) values[property.id] = described;
  }
  // A link from the record to itself (kg-gen puts every track on itself) says nothing on its page
  for (const [id, list] of Object.entries(values)) values[id] = list.filter((value) => value.value !== uri);
  const unavailable = new Set(resolved.unavailable);

  // Nested node values, one round trip per nested node shape.
  const nestedShapes: Record<string, ViewNodeShape> = {};
  const subjectsByShape = new Map<string, Set<string>>();
  for (const property of properties) {
    if (property.viewer !== DASH.DetailsViewer) continue;
    const nested = nestedNodeShape(shapes, property);
    if (!nested) continue;
    nestedShapes[property.id] = nested;
    const subjects = subjectsByShape.get(nested.id) ?? new Set<string>();
    for (const value of values[property.id] ?? []) {
      if (value.type === 'uri') subjects.add(value.value);
    }
    subjectsByShape.set(nested.id, subjects);
  }

  const nestedValues: Record<string, SubjectValues> = {};
  await Promise.all(
    Array.from(subjectsByShape.entries()).map(async ([shapeId, subjects]) => {
      const nested = shapes.nodeShapesById.get(shapeId);
      if (!nested || subjects.size === 0) return;
      const result = await resolvePropertyValues(
        Array.from(subjects),
        nested.properties.filter((property) => !property.hidden)
      );
      Object.assign(nestedValues, result.values);
    })
  );

  return {
    status: 'ready',
    uri,
    shapes,
    nodeShapes,
    properties,
    allProperties,
    rows,
    tables,
    values,
    nestedShapes,
    nestedValues,
    unavailable,
    coveredPredicates,
  };
}

/**
 * Whether the entity's class opens in the overview (dview:overview on one of its node shapes); null
 * while the shapes load. Classes without it open in the technical view.
 */
export function useOverviewDefault(entityProperties: EntityProperty[]): boolean | null {
  const [ready, setReady] = useState<boolean | null>(null);
  useEffect(() => {
    const types = entityProperties.find((property) => property.predicate === RDF_TYPE)?.objects.map((o) => o.value) ?? [];
    if (types.length === 0) return;
    let cancelled = false;
    loadViewShapes()
      .then((shapes) => {
        if (!cancelled) setReady(selectNodeShapes(shapes, types).some((shape) => shape.settings?.overview?.includes('true')));
      })
      .catch(() => !cancelled && setReady(false));
    return () => {
      cancelled = true;
    };
  }, [entityProperties]);
  return ready;
}

/**
 * @param uri - Entity IRI
 * @param entityProperties - DESCRIBE result of the entity (provides rdf:type)
 * @param enabled - false skips all loading (technical view, entity still loading)
 */
export function useEntityView(
  uri: string,
  entityProperties: EntityProperty[],
  enabled: boolean
): EntityViewModel {
  const [model, setModel] = useState<EntityViewModel>(EMPTY);

  useEffect(() => {
    if (!enabled || !uri) {
      setModel(EMPTY);
      return;
    }

    let cancelled = false;
    setModel({ ...EMPTY, status: 'loading', uri });
    buildModel(uri, entityProperties)
      .then((result) => {
        if (!cancelled) setModel(result);
      })
      .catch((error: unknown) => {
        console.error('[useEntityView] Failed to build the entity view:', error);
        if (!cancelled) setModel({ ...EMPTY, status: 'error', uri });
      });

    return () => {
      cancelled = true;
    };
  }, [uri, entityProperties, enabled]);

  return model;
}
